import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  countriesByName, countryName, letterFrame, lettersGo, placeKey, routesFor,
  type CustomerAudience, type GroupView, type SetupResponse,
} from "@joogoos/contracts";
import { api } from "../api.js";
import { liveGroups, SETUP, useSetup } from "./groups.js";

/**
 * **Customers → New group** (38, ADR 0068): three questions — who, where, and what its letter starts from.
 *
 * It was a form opened inside Set up's grid, and it started with every place the workspace looks in ticked, so a
 * group meant for the UK was also looked for in France and the Gulf unless somebody unticked them (2026-11-04). It
 * starts with none now, and a group with no place is not made: it would be a group nothing ever searches.
 *
 * *A copy of another group* copies its letters as they are — rules, offers, asks and languages (38 step 2) — for the
 * owner to read on the new group's Letters tab before anything is written with them. Nothing here asks a model.
 */

type From = { kind: "usual" } | { kind: "copy"; id: string };

export function NewGroup({ onMade, onCancel }: { onMade: (id: string) => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const q = useSetup();
  const keep = (d: SetupResponse) => qc.setQueryData(SETUP, d);
  const [name, setName] = useState("");
  const [looksFor, setLooksFor] = useState("");
  const [audience, setAudience] = useState<CustomerAudience>("business");
  const [route, setRoute] = useState<GroupView["route"]>("direct");
  const [places, setPlaces] = useState<string[]>([]);
  const [adding, setAdding] = useState("");
  const [from, setFrom] = useState<From>({ kind: "usual" });

  const addPlace = useMutation({
    mutationFn: (country: string) => api.addMarket(country),
    onSuccess: (d, country) => { keep(d); setPlaces((now) => [...new Set([...now, country])]); setAdding(""); },
  });
  const make = useMutation({
    mutationFn: async () => {
      const before = new Set((q.data?.groups ?? []).map((g) => g.id));
      const allowed = routesFor(audience);
      let d = await api.saveGroup({
        name, looksFor, audience, route: allowed.includes(route) ? route : allowed[0], countries: places,
        ...(from.kind === "copy" ? { copyLettersFrom: from.id } : {}),
      });
      const made = d.groups.find((g) => !before.has(g.id));
      // A copy lands in the folder of the group it was copied from: a second UK group is filed with the first.
      const source = from.kind === "copy" ? d.groups.find((g) => g.id === from.id) : undefined;
      if (made && source?.folder) d = await api.markGroups({ ids: [made.id], folder: source.folder });
      return { d, id: made?.id ?? null };
    },
    onSuccess: ({ d, id }) => { keep(d); if (id) onMade(id); },
  });

  const d = q.data;
  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!d) return <p className="text-sm text-bad">{String(q.error ?? "That did not load.")}</p>;
  if (!d.mayEdit) return <p className="text-[13px] text-muted">Only an owner of this workspace makes a group.</p>;

  const others = liveGroups(d);
  const choose = (f: From) => setFrom(f);
  const toggle = (k: string) => setPlaces((now) => now.includes(k) ? now.filter((p) => p !== k) : [...now, k]);
  const ready = name.trim().length >= 2 && looksFor.trim().length >= 5 && places.length > 0;
  const allowed = routesFor(audience);
  const law = adding ? lettersGo(adding) : null;

  return (
    <form className="flex flex-col gap-4 max-w-[700px]" onSubmit={(e) => { e.preventDefault(); if (ready) make.mutate(); }}>
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="text-base font-semibold">New group</h1>
        <span className="text-[12px] text-muted">three questions, then Joogoos can start finding</span>
      </div>

      <Question n={1} title="Who">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 80))} placeholder="UK cafés and delis"
            className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Looks for</span>
          <textarea value={looksFor} onChange={(e) => setLooksFor(e.target.value)} rows={2}
            placeholder="independent cafés that serve espresso and do not roast their own"
            className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]" />
        </label>
        <div className="flex flex-wrap gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Who they are</span>
            <select value={audience} onChange={(e) => setAudience(e.target.value as CustomerAudience)} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]">
              <option value="business">a business</option>
              <option value="public">the public</option>
            </select>
          </label>
          {audience === "public" ? (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">How we reach them</span>
              <select value={allowed.includes(route) ? route : allowed[0]} onChange={(e) => setRoute(e.target.value as GroupView["route"])} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]">
                {allowed.map((r) => <option key={r} value={r}>{r === "through_business" ? "through the businesses that hold them" : "a list you bring"}</option>)}
              </select>
            </label>
          ) : null}
        </div>
        {audience === "public" ? (
          <p className="text-xs text-muted leading-relaxed">
            Writing to a person who has not asked is unlawful in nearly every market, so this group is never written to
            directly. Its letter goes to the businesses that already have these people, and asks them something of its own.
          </p>
        ) : null}
      </Question>

      <Question n={2} title="Where">
        <div className="flex flex-wrap gap-1.5 items-center">
          {d.markets.map((m) => {
            const k = placeKey(m);
            return (
              <button key={k} type="button" aria-pressed={places.includes(k)} onClick={() => toggle(k)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${places.includes(k) ? "bg-ink text-white border-ink" : "bg-panel border-line-2 text-muted"}`}
              >{m.town ? `${m.town}, ${countryName(m.country)}` : countryName(m.country)}</button>
            );
          })}
          <select value={adding} aria-label="Another country" disabled={addPlace.isPending}
            onChange={(e) => setAdding(e.target.value)}
            className="px-2 py-1 rounded-lg border border-dashed border-line-2 bg-panel text-xs">
            <option value="">Another country…</option>
            {countriesByName().filter((c) => !d.markets.some((m) => m.country === c.code && !m.town)).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          {adding ? <button type="button" onClick={() => addPlace.mutate(adding)} disabled={addPlace.isPending} className="text-xs font-medium text-coral">Add</button> : null}
        </div>
        {law && !law.goes ? <p role="status" className="text-xs text-accent leading-relaxed">{law.says} You can still add it, to find companies for an introduction or a call.</p> : null}
        <p className="text-[11.5px] text-faint">Nothing is ticked until you tick it. A town is added in Settings.</p>
      </Question>

      <Question n={3} title="Its letter starts from">
        <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Its letter starts from">
          <Choice on={from.kind === "usual"} onPick={() => choose({ kind: "usual" })}
            title="Your usual letter" note="the one in Settings; you can write this group's own on its Letters tab" />
          {others.map((g) => (
            <Choice key={g.id} on={from.kind === "copy" && from.id === g.id} onPick={() => choose({ kind: "copy", id: g.id })}
              title={`A copy of ${g.name}`}
              note={copyNote(g)} />
          ))}
        </div>
        {from.kind === "copy" ? (
          <p className="text-[11.5px] text-faint">
            Its letters are copied as they are, rules and all. They go out exactly as written, so read them on the new
            group's Letters tab: if this group reads another language, nothing translates them for you yet.
          </p>
        ) : null}
      </Question>

      {make.error ? <p className="text-xs text-bad">{String(make.error)}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={!ready || make.isPending} className="px-3 py-1.5 rounded-lg bg-ink text-white text-[12.5px] font-semibold disabled:opacity-40">Make the group</button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-line-2 text-[12.5px] font-medium">Back</button>
        <span className="text-[11.5px] text-muted">
          {ready ? "Nothing is sent until you approve its first letter on Today." : "A name, a line saying what to look for, and at least one place."}
        </span>
      </div>
    </form>
  );
}

/** What copying a group's letters brings, said before it is chosen. */
function copyNote(g: GroupView): string {
  const own = g.letters.filter((l) => l.offer.trim() || l.ask.trim() || l.language);
  const langs = [...new Set(g.letters.map((l) => l.language).filter(Boolean))].map((l) => letterFrame(l).name);
  if (!own.length) return "it says your usual letter too";
  const n = g.letters.length;
  return `${n === 1 ? "its letter" : `its ${n} letters`} as ${n === 1 ? "it is" : "they are"}${langs.length ? `, in ${langs.join(" and ")}` : ""}, for you to read`;
}

function Question({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-line bg-panel p-4 flex flex-col gap-2.5">
      <h2 className="flex items-center gap-2.5 text-[13.5px] font-semibold">
        <span className="w-[20px] h-[20px] rounded-full bg-ink text-white text-[10.5px] font-bold grid place-items-center">{n}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Choice({ on, onPick, title, note }: { on: boolean; onPick: () => void; title: string; note: string }) {
  return (
    <button type="button" role="radio" aria-checked={on} onClick={onPick}
      className={`text-left rounded-lg border px-3 py-2 flex flex-col ${on ? "border-ink shadow-[0_0_0_1px_var(--color-ink)] bg-panel" : "border-line-2 bg-page"}`}>
      <span className="text-[13px] font-semibold">{title}</span>
      <span className="text-[11.5px] text-muted">{note}</span>
    </button>
  );
}
